import { useState, useCallback, useEffect, useRef } from "react";
import { Intercom, update as intercomUpdate } from "@intercom/messenger-js-sdk";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/shared/lib/supabase";

import type { IntercomCustomAttributes } from "@/shared/hooks/useIntercomAttributes";

interface UseIntercomSidePanelProps {
  user: User | null;
  isUserLoading: boolean;
  customAttributes?: IntercomCustomAttributes | null;
  autoBoot: boolean;
}

function pushAttributes(attributes: IntercomCustomAttributes) {
  const filtered: Record<string, string> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value !== null && value !== undefined) {
      filtered[key] = value;
    }
  }

  intercomUpdate(filtered);
}

export function useIntercomSidePanel({
  user,
  isUserLoading,
  customAttributes,
  autoBoot,
}: UseIntercomSidePanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userJWT, setUserJWT] = useState<string | null>(null);
  const attributesPushedRef = useRef(false);

  const initializeIntercom = useCallback(() => {
    if (!isUserLoading && user && !isInitialized && userJWT) {
      const firstName = user.user_metadata?.firstName || "";
      const lastName = user.user_metadata?.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim() || user.user_metadata?.full_name || "";

      try {
        Intercom({
          app_id: import.meta.env.VITE_INTERCOM_APP_ID || "zy5jj4es",
          user_id: user.id,
          name: fullName || user.email || "User",
          email: user.email || "",
          intercom_user_jwt: userJWT || undefined,
          hide_default_launcher: true,
          alignment: "right",
          horizontal_padding: 0,
          vertical_padding: 0,
        });
      } catch (error) {
        console.error("Error initializing Intercom:", error);
      }

      setTimeout(() => {
        if (window.Intercom) {
          window.Intercom("hide");
        }
      }, 100);

      setIsInitialized(true);
    }
  }, [user, isUserLoading, userJWT, isInitialized]);

  const openSidePanel = useCallback(() => {
    if (!isInitialized) {
      initializeIntercom();
    }

    if (!window.Intercom && user) {
      try {
        Intercom({
          app_id: import.meta.env.VITE_INTERCOM_APP_ID || "zy5jj4es",
          user_id: user.id,
          name: user.email || "User",
          email: user.email || "",
          hide_default_launcher: true,
        });
      } catch (error) {
        console.error("Force init error:", error);
      }
    }

    if (window.Intercom) {
      try {
        window.Intercom("show");
      } catch (error) {
        console.error("Error calling Intercom show:", error);
      }
    }

    setIsOpen(true);
  }, [isInitialized, initializeIntercom, user]);

  const closeSidePanel = useCallback(() => {
    setIsOpen(false);
    if (window.Intercom) {
      window.Intercom("hide");
    }
  }, []);

  const openWithNewMessage = useCallback(
    (prePopulatedMessage?: string) => {
      if (!isInitialized) {
        initializeIntercom();
      }
      setIsOpen(true);

      setTimeout(() => {
        if (window.Intercom) {
          if (prePopulatedMessage) {
            window.Intercom("showNewMessage", prePopulatedMessage);
          } else {
            window.Intercom("showNewMessage");
          }
        }
      }, 100);
    },
    [isInitialized, initializeIntercom],
  );

  // Fetch user JWT when user is available
  useEffect(() => {
    const fetchUserJWT = async () => {
      if (user && !userJWT) {
        try {
          const { data, error } = await supabase.functions.invoke("security/intercom-hash");
          if (error) {
            console.error("Error fetching Intercom JWT:", error);
          } else if (data?.intercom_user_jwt) {
            setUserJWT(data.intercom_user_jwt);
          }
        } catch (error) {
          console.error("Error fetching Intercom JWT:", error);
        }
      }
    };

    fetchUserJWT();
  }, [user, userJWT]);

  useEffect(() => {
    if (autoBoot) {
      initializeIntercom();
    }
  }, [autoBoot, initializeIntercom]);

  // Push custom attributes once Intercom is initialized and attributes are ready
  // Re-push whenever attributes change
  useEffect(() => {
    if (!isInitialized || !customAttributes || !window.Intercom) return;

    // Small delay to ensure Intercom is fully ready after boot
    const timeoutId = setTimeout(
      () => {
        pushAttributes(customAttributes);
        attributesPushedRef.current = true;
      },
      attributesPushedRef.current ? 0 : 500,
    );

    return () => clearTimeout(timeoutId);
  }, [isInitialized, customAttributes]);

  return {
    isOpen,
    openSidePanel,
    closeSidePanel,
    openWithNewMessage,
    isInitialized,
  };
}
